import { useWatch } from "react-hook-form";
import type { Control } from "react-hook-form";
import type { CLEN } from "@/models.ts";

function getProgramYearDateRange(
  programYear: string | undefined
): { start: string; end: string } | null {
  if (!programYear) return null;

  const match = programYear.match(/^(\d{4})-(\d{2,4})$/);
  if (!match) return null;

  const startYear = parseInt(match[1], 10);
  const endYearSuffix = match[2];
  const endYear =
    endYearSuffix.length === 2
      ? parseInt(`${Math.floor(startYear / 100)}${endYearSuffix}`, 10)
      : parseInt(endYearSuffix, 10);

  return {
    start: `${startYear}-07-01`,
    end: `${endYear}-06-30`,
  };
}

function isWithinProgramYear(
  dateStr: string | undefined,
  programYear: string | undefined
): boolean {
  const range = getProgramYearDateRange(programYear);
  if (!range || !dateStr) return true;

  return dateStr >= range.start && dateStr <= range.end;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDateToString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function useCLENFormLogic(control: Control<CLEN>) {
  const watchPreschoolCode = useWatch({ control, name: "PreschoolCode" });
  const watchProgramYear = useWatch({ control, name: "ProgramYear" });
  const watchLocalClassroomId = useWatch({ control, name: "LocalClassroomId" });
  const watchCSPPID = useWatch({ control, name: "CSPPID" });
  const watchLocalChildId = useWatch({ control, name: "LocalChildId" });
  const watchClassroomEnrollmentStart = useWatch({
    control,
    name: "ClassroomEnrollmentStart",
  });

  const validateEnrollmentStart = (value: string | undefined) => {
    if (!value) return "Classroom Enrollment Start Date is required";

    if (!isWithinProgramYear(value, watchProgramYear)) {
      return "Classroom Enrollment Start Date must be within the program year";
    }

    const maxDateStr = formatDateToString(addDays(new Date(), 45));
    if (value > maxDateStr) {
      return "Classroom Enrollment Start Date cannot be more than 45 days in the future";
    }

    return true;
  };

  const validateEnrollmentEnd = (value: string | undefined) => {
    if (!value) return true;

    if (
      watchClassroomEnrollmentStart &&
      value < watchClassroomEnrollmentStart
    ) {
      return "Classroom Enrollment End Date must be on or after the Classroom Enrollment Start DateEnrollment Start Date";
    }

    if (!isWithinProgramYear(value, watchProgramYear)) {
      return "Classroom Enrollment End Date must be within the program year";
    }

    return true;
  };

  return {
    watchedFields: {
      watchPreschoolCode,
      watchProgramYear,
      watchLocalClassroomId,
      watchCSPPID,
      watchLocalChildId,
      watchClassroomEnrollmentStart,
    },
    validators: {
      validateEnrollmentStart,
      validateEnrollmentEnd,
    },
  };
}
